import {Helmet} from '@dr.pogodin/react-helmet';
import {DataStatus} from 'platform/components';
import {Show} from 'platform/foundation';
import {ZodSchema} from 'zod';

import {useCallback, useEffect, useRef, useState} from 'react';

import {isNil, omit} from 'ramda';
import {isNilOrEmpty, isNotNil} from 'ramda-adjunct';

import {
  ConditionEnum,
  SaleVehicleAlbumPhotoResponseBody,
  useCloneSaleVehicleV2Mutation,
  useGetActiveSaleVehicleQuery,
  useGetSaleVehicleAlbumPhotoListQuery,
  useGetVehicleQuery,
  useListSaleVehicleAlbumsByVehicleQuery,
  VehicleTypeEnum,
} from '@dms/api/vehicle';
import {
  buildBatteryCertificateFileFields,
  SaleVehicle,
  SaleVehiclePhoto,
  SaleVehicleValidationSchema,
  SERVICE_INTERVALS_DEFAULT_VALUES,
  toAPIVehicle,
  toBaseVehicle,
  useFormNavigate,
  useGetCarFeatures,
  useIsVehicleQualitySealShown,
  useVehicleFormContext,
  useVehicleWarrantySync,
  VEHICLE_FORM_EVENTS,
  VehicleForm as VFKit,
} from '@dms/features/vehicle-forms-kit';
import i18n from '@dms/i18n';
import {
  formatDocumentTitle,
  handleApiError,
  queryParams,
  useMandatoryFields,
  FieldAvailabilityContext,
} from '@dms/shared';
import {AuditDataAssetsV2Body} from '@dms/teas';

import {useQueryState, useRequiredParams} from 'shared/web';

import {SaleVehicleForm} from '../components/SaleVehicleForm';
import {DEFAULT_VEHICLE_TYPE} from '../constants/vehicleConstants';
import {useAttachPhotosToSaleVehicleAlbum} from '../hooks/useAttachPhotosToSaleVehicleAlbum';
import {useConditionDataSubmit} from '../hooks/useConditionDataSubmit';
import {useCreatePurchaseVehicle} from '../hooks/useCreatePurchaseVehicle';
import {buildRegistrationPayload} from '../utils/buildRegistrationPayload';
import {getTargetAlbum} from '../utils/getTargetAlbum';

const TRUE_SEARCH_PARAM_VALUE = 'true';

const PresetPhotosEffect = ({
  albumPhotos,
}: {
  albumPhotos: SaleVehicleAlbumPhotoResponseBody[] | undefined;
}) => {
  const {sendMachineEvent, globalState} = useVehicleFormContext();
  const formReady = Boolean(globalState.context.form);
  useEffect(
    function presetAlbumPhotos() {
      if (!formReady || !albumPhotos || albumPhotos.length === 0) {
        return;
      }
      const mapped: Array<SaleVehiclePhoto> = albumPhotos.map((p) => ({
        fileId: p.fileId,
        fileUri: p.url,
      }));

      sendMachineEvent({type: VEHICLE_FORM_EVENTS.PHOTOS_PRESET_SUCCESS, photos: mapped});
    },
    [albumPhotos, sendMachineEvent, formReady]
  );
  return null;
};

export function CloneSaleVehicle() {
  const {navigateOnConfirm, navigateOnDiscard, isBusinessCaseReferrer} = useFormNavigate();
  const [copyPricesSearchParam] = useQueryState(queryParams.VEHICLE_CLONE_COPY_PRICES);
  const copyPrices = copyPricesSearchParam === TRUE_SEARCH_PARAM_VALUE;
  const {id: vehicleId} = useRequiredParams();
  const isQualitySealShown = useIsVehicleQualitySealShown(vehicleId);

  const {
    generateSchema,
    isMandatoryFieldsEnabled,
    isLoading: isMandatoryFieldsLoading,
  } = useMandatoryFields();

  const {
    data: vehicle,
    isLoading: isLoadingVehicle,
    isError: isErrorVehicle,
  } = useGetVehicleQuery(
    {vehicleId},
    {
      skip: isNilOrEmpty(vehicleId),
    }
  );

  const {
    isLoading: isSaleVehicleLoading,
    salePresetId,
    vehicleSourceCodeId,
    qualitySealKey,
    qualitySealManual,
  } = useGetActiveSaleVehicleQuery(
    {vehicleId},
    {
      skip: isNilOrEmpty(vehicleId),
      selectFromResult: ({data, isLoading}) => ({
        isLoading,
        vehicleSourceCodeId: data?.vehicleSourceCodeId,
        salePresetId: data?.salePresetId,
        qualitySealKey: data?.qualitySealKey,
        qualitySealManual: data?.qualitySealManual,
        saleVehicleId: data?.id,
      }),
    }
  );

  const {saleVehicleAlbum, isLoading: isLoadingSaleVehicleAlbum} =
    useListSaleVehicleAlbumsByVehicleQuery(
      {vehicleId},
      {
        skip: isNilOrEmpty(vehicleId),
        selectFromResult: ({data, isLoading}) => ({
          isLoading,
          data,
          saleVehicleAlbum: data?.find(
            (album) => album.active && album.name === 'PHOTOS_CAR_SALES'
          ),
        }),
      }
    );

  const {data: saleVehicleAlbumPhotos} = useGetSaleVehicleAlbumPhotoListQuery(
    {
      saleVehicleId: saleVehicleAlbum?.saleVehicleId ?? '',
      saleVehicleAlbumId: saleVehicleAlbum?.id ?? '',
    },
    {
      skip: isNilOrEmpty(saleVehicleAlbum),
    }
  );

  const currentContextRef = useRef<FieldAvailabilityContext>({
    vehicleType: DEFAULT_VEHICLE_TYPE,
  });

  const [formSchema, setFormSchema] = useState<ZodSchema<SaleVehicle>>(() =>
    generateSchema(SaleVehicleValidationSchema, currentContextRef.current)
  );

  useEffect(
    function updateFormSchemaForVehicle() {
      if (vehicle) {
        currentContextRef.current = {
          ...currentContextRef.current,
          vehicleType: (vehicle.type as VehicleTypeEnum) ?? DEFAULT_VEHICLE_TYPE,
        };
        setFormSchema(
          generateSchema(
            SaleVehicleValidationSchema,
            currentContextRef.current,
            (vehicle.condition as ConditionEnum) ?? undefined
          )
        );
      }
    },
    [vehicle?.type, vehicle?.condition, generateSchema] // eslint-disable-line react/exhaustive-deps -- Muted pre-existing violation
  );

  const [getCarFeatures] = useGetCarFeatures();

  const [cloneSaleVehicleMutation, {isLoading: isCreating}] = useCloneSaleVehicleV2Mutation();

  const {handleSubmit: handleConditionDataSubmit} = useConditionDataSubmit();
  const {attachToBusinessCase} = useCreatePurchaseVehicle();

  const {attachPhotos} = useAttachPhotosToSaleVehicleAlbum();

  const {syncWarranties, isSyncing} = useVehicleWarrantySync();

  const handleVehicleTypeChange = useCallback(
    (type: VehicleTypeEnum, condition?: ConditionEnum) => {
      currentContextRef.current = {...currentContextRef.current, vehicleType: type};
      const newSchema = generateSchema(
        SaleVehicleValidationSchema,
        currentContextRef.current,
        condition
      );
      setFormSchema(newSchema);
    },
    [generateSchema]
  );

  const handleFuelTypeChange = useCallback(
    (fuelTypeCondition: FieldAvailabilityContext) => {
      currentContextRef.current = {...currentContextRef.current, ...fuelTypeCondition};
      const newSchema = generateSchema(SaleVehicleValidationSchema, currentContextRef.current);
      setFormSchema(newSchema);
    },
    [generateSchema]
  );

  const handleSave = async (values: SaleVehicle) => {
    const sourcingData = await getCarFeatures(values);
    const apiPayload = toAPIVehicle(values, null, sourcingData);

    const onCreate = () =>
      cloneSaleVehicleMutation({
        cloneSaleVehicleV2RequestBody: {
          vehicle: apiPayload,
          registration: buildRegistrationPayload(values, false, isQualitySealShown),
          source: {vehicleId, copyCustomFields: true},
          additionalNote: values.additionalNote,
          copyPrices,
        },
        validateMandatoryFields: isMandatoryFieldsEnabled(values.condition?.[0] ?? undefined),
        fileFields: buildBatteryCertificateFileFields(values.batteryCertificateDocument),
      })
        .unwrap()
        .then((clonedVehicle) => clonedVehicle.vehicleId)
        .then(async (id) => {
          await attachPhotos(id, values.photos, getTargetAlbum(values.targetAlbum));
          return id;
        })
        .then(async (id) => {
          await handleConditionDataSubmit(
            id,
            omit(['sessionId'], values.auditData ?? {}) ?? {},
            (values.auditAssets as Record<string, AuditDataAssetsV2Body>) ?? {}
          );
          return id;
        })
        .then(async (id) => {
          await syncWarranties({vehicleId: id, warranties: values.warranties ?? []});
          return id;
        })
        .then(async (id) => {
          if (isBusinessCaseReferrer) {
            await attachToBusinessCase(id);
          }
          navigateOnConfirm(id);
        });

    // eslint-disable-next-line eslint-js/no-restricted-syntax
    return onCreate().catch(handleApiError);
  };

  const isLoading =
    isMandatoryFieldsLoading ||
    isLoadingVehicle ||
    isSaleVehicleLoading ||
    isLoadingSaleVehicleAlbum;
  const isError = isErrorVehicle || isNilOrEmpty(vehicleId);

  const baseVehicle = isNotNil(vehicle)
    ? toBaseVehicle(omit(['registrationPlate', 'vin'], vehicle))
    : undefined;

  return (
    <>
      <Helmet>
        <title>
          {formatDocumentTitle(
            i18n.t('entity.vehicle.labels.clone'),
            i18n.t('entity.vehicle.labels.pageTitle')
          )}
        </title>
      </Helmet>
      <DataStatus
        isLoading={isLoading}
        isError={isError}
        isEmpty={isNil(baseVehicle)}
        emptyMessage={i18n.t('page.vehicle.notifications.notFound')}
        action={{
          title: i18n.t('general.actions.back'),
          leftIcon: 'navigation/chevron_left',
          onClick: navigateOnDiscard,
        }}
        data-testid="cloneSaleVehicle-notFound"
      >
        <VFKit.Form
          showMandatoryFields
          experimentalZodSchema={formSchema}
          defaultValues={{
            ...baseVehicle,
            targetAlbum: ['PHOTOS_CAR_SALES'],
            serviceIntervals: {
              ...SERVICE_INTERVALS_DEFAULT_VALUES,
              ...vehicle?.serviceIntervals,
            },
            salePresetId,
            vehicleSourceCodeId,
            qualitySealKey,
            isQualitySealManualOverride: qualitySealManual,
            warranties: [],
          }}
        >
          <Show when={saleVehicleAlbumPhotos && saleVehicleAlbumPhotos.length > 0}>
            <PresetPhotosEffect albumPhotos={saleVehicleAlbumPhotos ?? []} />
          </Show>
          <SaleVehicleForm
            onSave={handleSave}
            onVehicleTypeChange={handleVehicleTypeChange}
            onFuelTypeChange={handleFuelTypeChange}
            onDiscard={navigateOnDiscard}
            isCreating={isCreating || isSyncing}
            vehicleId={vehicleId}
            shouldShowWarranties
            tabRoutePattern="cloneV2Tab"
            modalKeys={{
              headline: i18n.t('entity.vehicle.labels.clone'),
              create: i18n.t('entity.vehicle.labels.clone'),
            }}
          />
        </VFKit.Form>
      </DataStatus>
    </>
  );
}
